import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  swalConfirm,
  swalToastWarning
} from 'components/utilities/SwalToastUtil';
import ProductStartsWithDropdown from 'components/dropdowns/ProductStartsWithDropdown';
import { checkExistingRequest } from 'services/stock-location/stockRequestService';

const emptyRow = {
  productId: null,
  emplasCode: '',
  supplierCode: '',
  description: '',
  uom: '',
  stockQty: '',
  boxQty: '',
  reqQty: ''
};

const NewStockRequestTable = ({ area, rows, setRows }) => {
  const { t } = useTranslation();
  const [newRow, setNewRow] = useState({ ...emptyRow });
  const [isChecking, setIsChecking] = useState(false);

  const applyProductMatch = product => {
    if (!product) {
      setNewRow(prev => ({
        ...prev,
        productId: null,
        emplasCode: '',
        supplierCode: '',
        description: '',
        uom: '',
        stockQty: '',
        boxQty: ''
      }));
      return;
    }
    setNewRow(prev => ({
      ...prev,
      productId: product.productId,
      emplasCode: product.productCode ?? '',
      supplierCode: product.supplierCode ?? '',
      description: product.productDescription ?? '',
      uom: product.uom ?? '',
      stockQty: product.mUnitStock ?? '',
      boxQty: product.packSize ?? ''
    }));
  };

  const commitRow = () => {
    setRows(prev => [...prev, newRow]);
    setNewRow({ ...emptyRow });
  };

  const clearReqQty = () => {
    setNewRow(prev => ({ ...prev, reqQty: '' }));
  };

  const handleReqQtyBlur = async () => {
    const isRowComplete =
      newRow.productId &&
      newRow.emplasCode &&
      newRow.supplierCode &&
      newRow.description &&
      newRow.reqQty &&
      Number(newRow.reqQty) > 0;

    if (!isRowComplete) return;

    if (!area) {
      swalToastWarning(t('selectAreaFirst'));
      return;
    }

    setIsChecking(true);
    try {
      const res = await checkExistingRequest(newRow.productId, area?.value);
      if (res?.data?.exists) {
        swalConfirm(
          t('duplicateRequestConfirmTitle'),
          t('duplicateRequestConfirmText'),
          commitRow,
          clearReqQty
        );
      } else {
        commitRow();
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handleRemoveRow = index => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleReqQtyChange = value => {
    setNewRow(prev => ({ ...prev, reqQty: value.replace(/[^0-9]/g, '') }));
  };

  const inputsDisabled = isChecking;

  return (
    <Table bordered hover responsive>
      <thead className="bg-200 text-900 align-middle">
        <tr>
          <th>{t('modalColEmplasCode')}</th>
          <th>{t('modalColSupplierCode')}</th>
          <th style={{ minWidth: 280 }}>{t('modalColDescription')}</th>
          <th>{t('modalColUom')}</th>
          <th>{t('modalColStockQty')}</th>
          <th>{t('modalColBoxQty')}</th>
          <th>{t('modalColReqQty')}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <td>
              <Form.Control value={row.emplasCode} disabled />
            </td>
            <td>
              <Form.Control value={row.supplierCode} disabled />
            </td>
            <td style={{ minWidth: 280 }}>
              <Form.Control value={row.description} disabled />
            </td>
            <td>
              <Form.Control value={row.uom} disabled />
            </td>
            <td>
              <Form.Control value={row.stockQty} disabled />
            </td>
            <td>
              <Form.Control value={row.boxQty} disabled />
            </td>
            <td>
              <Form.Control value={row.reqQty} disabled />
            </td>
            <td>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemoveRow(index)}
              >
                {t('modalRemove')}
              </Button>
            </td>
          </tr>
        ))}

        <tr>
          <td>
            <ProductStartsWithDropdown
              field="productCode"
              value={newRow.emplasCode}
              isDisabled={inputsDisabled}
              onSelect={applyProductMatch}
              placeholder={t('searchPlaceholder')}
            />
          </td>
          <td>
            <ProductStartsWithDropdown
              field="supplierCode"
              value={newRow.supplierCode}
              isDisabled={inputsDisabled}
              onSelect={applyProductMatch}
              placeholder={t('searchPlaceholder')}
            />
          </td>
          <td style={{ minWidth: 280 }}>
            <ProductStartsWithDropdown
              field="description"
              value={newRow.description}
              isDisabled={inputsDisabled}
              onSelect={applyProductMatch}
              placeholder={t('searchPlaceholder')}
            />
          </td>
          <td>
            <Form.Control value={newRow.uom} disabled />
          </td>
          <td>
            <Form.Control value={newRow.stockQty} disabled />
          </td>
          <td>
            <Form.Control value={newRow.boxQty} disabled />
          </td>
          <td>
            <Form.Control
              type="text"
              value={newRow.reqQty}
              disabled={inputsDisabled}
              onChange={e => handleReqQtyChange(e.target.value)}
              onBlur={handleReqQtyBlur}
            />
          </td>
          <td></td>
        </tr>
      </tbody>
    </Table>
  );
};

NewStockRequestTable.propTypes = {
  area: PropTypes.object,
  rows: PropTypes.array,
  setRows: PropTypes.func
};

export default NewStockRequestTable;
